import {
  CommandBar,
  DirectionalHint,
  getTheme,
  ICommandBarItemProps,
  mergeStyleSets
} from "@fluentui/react";
import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { CustomMenuItem, MenuButton } from "./CustomButtons";

import { CustomButton } from ".";

import {
  CloseSidebarMenu,
  OpenSidebarMenu
} from "@/common/actions/sidebarMenu";
import { useGetProfile } from "@/core/libs/api/react-query/index";
import { useAuth } from "@/core/libs/use-auth";
import { RootState } from "@/core/store/rootReducer";
import { useNotifications } from "@/hooks/useNotifications";
import { useOrganization } from "@/hooks/useOrganization";
import { useSelectedBu } from "@/hooks/useSelectedBu";
import { dismissProfile } from "@/modules/User/actions/profile";
import {
  BusinessUnit,
  BusinessUnitRegion,
  BusinessUnitSegment
} from "@/modules/User/types";

const theme = getTheme();

function getLeftSplitDividerClassNames() {
  return mergeStyleSets({
    wrapper: {
      display: "inline-flex",
      height: "100%",
      alignItems: "center",
      position: "absolute",
      left: 28
    },
    divider: {
      width: 1,
      height: 16,
      backgroundColor: theme.palette.neutralTertiaryAlt
    }
  });
}

const splitButtonLeftItemProps = {
  styles: {
    splitContainer: { flexDirection: "row-reverse" as const }
  }
};

const noCheckmarkSubMenuStyles = {
  subComponentStyles: {
    menuItem: {
      checkmarkIcon: { display: "none" }
    }
  }
};

export function Header() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const {
    handleNotificationBubbleVisibility,
    openNotificationsBubbleButtonId,
    countUnreadNotifications
  } = useNotifications();

  const isMenuOpen = useSelector(
    (state: RootState) => state.sidebarMenu.isMenuOpen
  );

  const {
    selectedBU,
    handleSelectBu,
    handleSelectRegion,
    handleSelectSegment,
    selectedRegion,
    selectedSegment
  } = useSelectedBu();
  const userProfile = useGetProfile();
  const user = userProfile.data?.data;

  const { organizationSelected, openOrganizationSelection, organizations } =
    useOrganization();

  const items: ICommandBarItemProps[] = useMemo(
    () => [
      {
        key: "menu",
        iconOnly: true,
        ariaLabel: "Menu",
        commandBarButtonAs: MenuButton,
        onClick: () => {
          dispatch(isMenuOpen ? CloseSidebarMenu() : OpenSidebarMenu());
        },
        isMenuOpen: isMenuOpen
      },
      {
        key: "home",
        iconOnly: true,
        ariaLabel: "Libertas",
        iconProps: { iconName: "libertas" },
        title: "Voltar para Página Inicial",
        to: !selectedBU && selectedRegion ? "/contracts" : "/",
        commandBarButtonAs: CustomButton
      },
      {
        key: "hello",
        text: `Olá, ${user?.fullName || "usuário"}!`,
        title: `Seja bem-vindo ao Libertas! Clique para editar seu perfil.`,
        commandBarButtonAs: CustomButton,
        to: "/user/profile"
      }
    ],
    [dispatch, isMenuOpen, user?.fullName, selectedBU, selectedRegion]
  );

  const createBusinessUnitSubMenu = useCallback(
    (businessUnit: BusinessUnit): ICommandBarItemProps => {
      const randomSuffix = Math.random().toString(36).substring(2, 9);
      const businessUnitSelector: ICommandBarItemProps = {
        id: `businessUnit-selector-item-${businessUnit.id}-${randomSuffix}`,
        key: `bu-${businessUnit.id}-${randomSuffix}`,
        text: businessUnit?.name,
        onClick: () => {
          handleSelectBu(businessUnit.id);
        }
      };

      if (businessUnit?.children?.length > 0) {
        const sortedChildren = [...businessUnit.children].sort((a, b) =>
          a.name.localeCompare(b.name)
        );

        // split: true renders a split button — clicking the text selects
        // this BU (fires onClick), clicking the chevron opens the submenu.
        // canCheck: true fixes touch devices where the split button would
        // otherwise redirect all taps to open the submenu.
        businessUnitSelector.split = true;
        businessUnitSelector.canCheck = true;
        businessUnitSelector.submenuIconProps = { iconName: "ChevronLeft" };
        businessUnitSelector.itemProps = splitButtonLeftItemProps;
        businessUnitSelector.getSplitButtonVerticalDividerClassNames =
          getLeftSplitDividerClassNames;
        businessUnitSelector.subMenuProps = {
          directionalHint: DirectionalHint.leftTopEdge,
          styles: noCheckmarkSubMenuStyles,
          items: sortedChildren.map(child => createBusinessUnitSubMenu(child))
        };
      }

      return businessUnitSelector;
    },
    [handleSelectBu]
  );

  const createSegmentSubMenu = useCallback(
    (
      segment: BusinessUnitSegment,
      regionName: string
    ): ICommandBarItemProps => {
      return {
        key: `segment-${segment.segment}`,
        text: segment.segment,
        split: true,
        canCheck: true,
        submenuIconProps: { iconName: "ChevronLeft" },
        itemProps: splitButtonLeftItemProps,
        getSplitButtonVerticalDividerClassNames: getLeftSplitDividerClassNames,
        onClick: () => {
          handleSelectSegment(segment.segment, regionName);
          navigate("/contracts");
        },
        subMenuProps: {
          directionalHint: DirectionalHint.leftTopEdge,
          styles: noCheckmarkSubMenuStyles,
          items: segment.businessUnits.map(businessUnit =>
            createBusinessUnitSubMenu(businessUnit)
          )
        }
      };
    },
    [createBusinessUnitSubMenu, handleSelectSegment, navigate]
  );

  const createRegionSubMenu = useCallback(
    (region: BusinessUnitRegion): ICommandBarItemProps => {
      return {
        key: `region-${region.region}`,
        text: region.region,
        split: true,
        canCheck: true,
        submenuIconProps: { iconName: "ChevronLeft" },
        itemProps: splitButtonLeftItemProps,
        getSplitButtonVerticalDividerClassNames: getLeftSplitDividerClassNames,
        onClick: () => {
          handleSelectRegion(region.region);
          navigate("/contracts");
        },
        subMenuProps: {
          directionalHint: DirectionalHint.leftTopEdge,
          styles: noCheckmarkSubMenuStyles,
          items: region.segments.map(segment =>
            createSegmentSubMenu(segment, region.region)
          )
        }
      };
    },
    [createSegmentSubMenu, handleSelectRegion, navigate]
  );

  const farItems: ICommandBarItemProps[] = useMemo(() => {
    const items: ICommandBarItemProps[] = [];

    const organizationSelector: ICommandBarItemProps = {
      id: "organization-selector",
      key: "organizationSelector",
      ariaLabel: "Selecionar Organização",
      text: `Organização selecionada: ${organizationSelected?.name}`,
      onClick: () => {
        openOrganizationSelection();
      }
    };

    organizations &&
      organizations.length > 1 &&
      items.push(organizationSelector);

    const userHasBusinessUnits = user?.businessUnits?.length > 0;
    const userHasBusinessUnitClassification =
      user?.businessUnitClassification?.length > 0;

    if (
      userHasBusinessUnits ||
      userHasBusinessUnitClassification ||
      user?.businessUnit?.id
    ) {
      const flatBUs = user?.businessUnits ?? [];
      const classifiedBUs = user?.businessUnitClassification ?? [];
      const hasClassification = classifiedBUs.length > 0;

      const businessUnitDisplayName = selectedBU?.name;
      const breadcrumbParts: string[] = [];

      if (selectedRegion) breadcrumbParts.push(selectedRegion);

      if (selectedSegment) breadcrumbParts.push(selectedSegment);

      if (businessUnitDisplayName) {
        breadcrumbParts.push(businessUnitDisplayName);
      }

      const businessUnitSelector: ICommandBarItemProps = {
        id: "businessUnit-selector",
        key: "BUTreeView",
        ariaLabel: "Selecionar unidade de negócio",
        text:
          breadcrumbParts.length > 0
            ? breadcrumbParts.join(" > ")
            : "Selecionar Unidade de Negócio"
      };

      if (hasClassification) {
        // Modo de classificação: region > segment > Hierarquia de BUs
        const sortedRegions = [...classifiedBUs].sort((a, b) =>
          a.region.localeCompare(b.region)
        );

        businessUnitSelector.subMenuProps = {
          directionalHint: DirectionalHint.leftTopEdge,
          styles: noCheckmarkSubMenuStyles,
          items: sortedRegions.map(region => createRegionSubMenu(region))
        };
      } else if (flatBUs.length > 0) {
        // Modo Flat: árvore de BU simples, sem região ou segmento
        const hasOnlyOneBUWithoutChildren =
          flatBUs.length === 1 && !flatBUs[0]?.children?.length;

        if (!hasOnlyOneBUWithoutChildren) {
          const sortedBUs = [...flatBUs].sort((a, b) =>
            a.name.localeCompare(b.name)
          );

          businessUnitSelector.subMenuProps = {
            directionalHint: DirectionalHint.leftTopEdge,
            styles: noCheckmarkSubMenuStyles,
            items: sortedBUs.map(businessUnit =>
              createBusinessUnitSubMenu(businessUnit)
            )
          };
        }
      }

      items.push(businessUnitSelector);
    }

    const notificationOpenCount =
      countUnreadNotifications > 99 ? "+99" : countUnreadNotifications || "";

    return [
      ...items,
      {
        id: openNotificationsBubbleButtonId,
        key: "response-notifications",
        text: `${notificationOpenCount}`,
        onClick: () => {
          handleNotificationBubbleVisibility();
        },
        iconProps: {
          iconName: "Ringer",
          className: "notifications-icon",
          styles: {
            root: {
              backgroundColor: notificationOpenCount ? "#395ca3" : "",
              color: notificationOpenCount ? theme.palette.white : "#395ca3",
              borderRadius: "75%",
              height: "1.5rem",
              width: "1.5rem",
              lineHeight: "unset",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }
          }
        },
        buttonStyles: {
          iconHovered: {
            color: notificationOpenCount ? theme.palette.white : "#395ca3"
          },
          iconPressed: {
            color: notificationOpenCount ? theme.palette.white : "#395ca3"
          },

          textContainer: countUnreadNotifications !== 0 && {
            backgroundColor: "#B62021",
            color: theme.palette.white,
            display: "flex",
            alignItems: "center",
            borderRadius: "50%",
            fontSize: "0.75rem",
            height: "1rem",
            lineHeight: "unset",
            position: "absolute",
            top: "10%",
            right: "60%"
          }
        }
      },
      {
        key: "userConfiguration",
        ariaLabel: "Configurações de usuário",
        iconOnly: true,
        iconProps: { iconName: "PlayerSettings" },
        commandBarButtonAs: CustomButton,
        subMenuProps: {
          contextualMenuItemAs: CustomMenuItem,
          items: [
            {
              key: "userProfile",
              text: "Perfil",
              iconProps: { iconName: "ContactInfo" },
              to: "/user/profile"
            },
            {
              key: "userLogout",
              text: "Sair",
              iconProps: { iconName: "PlugDisconnected" },
              onClick: () => {
                logout();
                dispatch(dismissProfile());
                window.fcWidget?.destroy();
              }
            }
          ]
        }
      }
    ];
  }, [
    createBusinessUnitSubMenu,
    createRegionSubMenu,
    dispatch,
    logout,
    selectedBU,
    selectedRegion,
    selectedSegment,
    user,
    organizationSelected?.name,
    organizations,
    openOrganizationSelection,
    countUnreadNotifications,
    handleNotificationBubbleVisibility,
    openNotificationsBubbleButtonId
  ]);

  return (
    <CommandBar
      items={items}
      farItems={farItems}
      styles={{
        root: {
          borderBottom: "1px solid",
          borderBottomColor: theme.palette.neutralLight,
          minHeight: 48,
          paddingLeft: 10
        }
      }}
      overflowButtonProps={{
        menuProps: {
          items: [],
          contextualMenuItemAs: CustomMenuItem
        }
      }}
    />
  );
}
